import React, { useState, useEffect } from "react";
import axios from "../../../interceptor/axios-config";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import config from "../../../config";
import Swal from "sweetalert2";

// 🔥 Current Session
const getCurrentSession = () => {
  const year = new Date().getFullYear();
  return `${year}-${year + 1}`;
};

const AddFeeTemplateModal = ({ show, onHide, refresh }) => {

  const [classes, setClasses] = useState([]);
  const [gradeId, setGradeId] = useState("");
  const [selectedSections, setSelectedSections] = useState([]);
  const [session, setSession] = useState(getCurrentSession());

  const [items, setItems] = useState([
    { fee_name: "", amount: "", fee_mode: "one_time" }
  ]);

  // 🔥 Fetch normalized classes
  useEffect(() => {
    if (!show) return;

    axios
      .get(`${config.apiBaseUrl}/grades/classes.php?action=list`)
      .then((res) => {
        console.log("API RESPONSE:", res.data);

        if (Array.isArray(res.data)) {
          setClasses(res.data); // ✅ direct array
        } else if (res.data.success) {
          setClasses(res.data.data); // fallback if wrapped
        } else {
          setClasses([]);
        }
      })
      .catch((err) => {
        console.error("Class fetch error:", err);
        setClasses([]);
      });

  }, [show]);

  console.log(classes);

  // 🔥 Available sections based on grade
  const availableSections = gradeId
    ? [...new Set(
        classes
          .filter(c => c.grade_id == gradeId)
          .map(c => c.section_name)
      )]
    : [];

  // 🔥 Auto-check all sections when grade changes
  const handleGradeChange = (e) => {
    const selectedGrade = e.target.value;
    setGradeId(selectedGrade);

    if (selectedGrade) {
      const sectionsForGrade = [
        ...new Set(
          classes
            .filter(c => c.grade_id == selectedGrade)
            .map(c => c.section_name)
        )
      ];
      setSelectedSections(sectionsForGrade); // auto-check all
    } else {
      setSelectedSections([]);
    }
  };

  const toggleSection = (sec) => {
    setSelectedSections(prev =>
      prev.includes(sec)
        ? prev.filter(s => s !== sec)
        : [...prev, sec]
    );
  };

  const changeItem = (i, field, value) => {
    const updated = [...items];
    updated[i][field] = value;
    setItems(updated);
  };

  const addRow = () =>
    setItems([
      ...items,
      { fee_name: "", amount: "", fee_mode: "one_time" }
    ]);

  const removeRow = (i) =>
    setItems(items.filter((_, idx) => idx !== i));

  const handleSave = async () => {

    if (!gradeId) {
      Swal.fire("Error", "Please select class", "warning");
      return;
    }

    if (selectedSections.length === 0) {
      Swal.fire("Error", "Select at least one section", "warning");
      return;
    }

    if (items.some(it => !it.fee_name || !it.amount)) {
      Swal.fire("Error", "Fill all fee rows", "warning");
      return;
    }

    // 🔥 Get matching class_ids
    const selectedClassObjects = classes.filter(
      c =>
        c.grade_id == gradeId &&
        selectedSections.includes(c.section_name)
    );

    const classIds = selectedClassObjects.map(c => c.class_id);

    try {

      const payload = [];

      classIds.forEach(classId => {
        items.forEach(it => {
          payload.push({
            class_id: classId,
            fee_name: it.fee_name,
            amount: it.amount,
            fee_mode: it.fee_mode,
            session
          });
        });
      });

      const res = await axios.post(
        `${config.apiBaseUrl}/fees/fee_templates.php?action=add`,
        payload
      );

      if (!res.data.success) {
        Swal.fire("Error", res.data.error, "error");
        return;
      }

      Swal.fire("Success", "Template saved", "success");

      refresh();
      onHide();

    } catch (err) {
      Swal.fire("Error", "Server error", "error");
    }
  };

  // 🔥 Unique grades
  const uniqueGrades = [...new Map(
    classes.map(c => [c.grade_id, c])
  ).values()];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Fee Template</Modal.Title>
      </Modal.Header>

      <Modal.Body>

        {/* Grade + Session */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Label>Class</Form.Label>
            <Form.Select
              value={gradeId}
              onChange={handleGradeChange}
            >
              <option value="">Select Class</option>
              {uniqueGrades.map(g => (
                <option key={g.grade_id} value={g.grade_id}>
                  Class-{g.grade_name} ({g.academic_year})
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={6}>
            <Form.Label>Session</Form.Label>
            <Form.Control
              value={session}
              onChange={(e) => setSession(e.target.value)}
            />
          </Col>
        </Row>

        {/* Section Checkboxes */}
        {gradeId && (
          <Row className="mb-3">
            <Col>
              <Form.Label>Sections</Form.Label>
              <Row>
                {availableSections.map(sec => (
                  <Col md={3} key={sec}>
                    <Form.Check
                      type="checkbox"
                      label={sec}
                      checked={selectedSections.includes(sec)}
                      onChange={() => toggleSection(sec)}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        )}

        {/* Fee Items Header */}
        <Row className="fw-bold mb-1">
          <Col>Fee Name</Col>
          <Col>Amount</Col>
          <Col md={3}>Mode</Col>
          <Col xs="auto"></Col>
        </Row>

        {/* Fee Rows */}
        {items.map((it, i) => (
          <Row key={i} className="mb-2 align-items-center">

            <Col>
              <Form.Control
                placeholder="Fee Name"
                value={it.fee_name}
                onChange={(e) =>
                  changeItem(i, "fee_name", e.target.value)
                }
              />
            </Col>

            <Col>
              <Form.Control
                type="number"
                placeholder="Amount"
                value={it.amount}
                onChange={(e) =>
                  changeItem(i, "amount", e.target.value)
                }
              />
            </Col>

            <Col md={3}>
              <Form.Select
                value={it.fee_mode}
                onChange={(e) =>
                  changeItem(i, "fee_mode", e.target.value)
                }
              >
                <option value="one_time">One-time</option>
                <option value="monthly">Monthly</option>
              </Form.Select>
            </Col>

            <Col xs="auto">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeRow(i)}
              >
                ✕
              </Button>
            </Col>

          </Row>
        ))}

        <div className="text-end mt-2">
          <Button variant="outline-primary" size="sm" onClick={addRow}>
            + Add Fee Row
          </Button>
        </div>

      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button onClick={handleSave}>
          Save Template
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddFeeTemplateModal;